/**
 * Публичный отчёт бесплатного аудита: `/audit/:slug`.
 *
 * Маршрут **публичный** по определению: ссылку пересылают, и требовать по ней
 * вход бессмысленно.
 *
 * Что здесь было. Экран показывал балл, слово лестницы, одну строку основания
 * и кнопку — при том что `GET /public/audits/{slug}/report` уже отдавал разрез
 * по движкам, топ конкурентов, живые цитаты и AI-готовность сайта. Владелец
 * описал это как «там тока такое видно»: продукт меряет шесть вещей, а
 * показывает одну. Ниже собран весь отчёт.
 *
 * Порядок блоков задан §4 спеки и анатомией `components.md` («Public audit
 * report»): шапка → индекс одним числом → **цитаты, где бренд не назвали**
 * (это драма, ради которой отчёт читают) → конкуренты → движки → готовность
 * сайта → призыв.
 *
 * **Совместимость назад — обязательное свойство.** Отчёты живут семь дней, и в
 * хранилище лежат собранные прежним воркером: без `by_engine`, без
 * `readiness`, без `top_competitors`. Каждая секция ниже условная, и отчёт без
 * новых полей рендерится молча короче — а не падает.
 *
 * **Ширина каждой полосы стоит в разметке** (`--w`): отчёт печатают и читают
 * краулером, и полоса, ширина которой живёт в скрипте, для обоих пуста.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { Mark } from '../ui/Mark';
import { Empty, Skeleton } from '../ui/state';
import { api } from '../api/client';
import { ApiError } from '../api/problem';
import { getPublicAudit, type Audit } from '../api/audits';
import { AUDIT_SLUG_KEY } from './Audit';
import { rememberClaim } from '../pending-claim';
import { barGeometries } from '@shared/src/bars';
import {
  countOf,
  coverage,
  coverageOf,
  dateProse,
  NOT_MEASURED,
  percent,
  score as scoreFmt,
  statusLadder,
} from '@shared/src/format';
import { PUBLIC_REPORT_LIFETIME, engineName } from '@shared/src/product';
import type { components } from '../api/schema';

type AuditReport = components['schemas']['AuditReport'];
// Тип цитаты выводится из отчёта, а не берётся отдельной схемой: так он
// не может разойтись с тем, что реально приходит в `quotes`.
type AuditQuote = NonNullable<AuditReport['quotes']>[number];

/** Сводка аудита — источник марки, домена и полноты сетки. */
type Summary = Audit | null;

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; report: AuditReport; summary: Summary }
  | { kind: 'failed'; error: unknown };

/** Сколько цитат показываем: 3–5 характерных, а не весь протокол. */
const QUOTES_UNSEEN = 3;
const QUOTES_SEEN = 2;

export function AuditReportPage() {
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const proof = params.get('t') ?? '';
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  // Подтверждение адреса переходом по ссылке из письма.
  //
  // Отправляется ОТДЕЛЬНО от загрузки отчёта и молча: человек пришёл читать
  // отчёт, и отказ на служебном запросе не должен ни задерживать чтение, ни
  // появляться на экране сообщением, смысл которого читателю неизвестен. Ответ
  // сервера одинаков и для верного токена, и для чужого, поэтому показывать тут
  // нечего в принципе.
  //
  // Токен остаётся в адресе, а не вычищается после отправки: он же даёт право
  // присвоить аудит организации, и человек, обновивший вкладку или сохранивший
  // ссылку, это право терять не должен.
  useEffect(() => {
    if (!proof) return;
    const abort = new AbortController();
    void api
      .post(
        `/public/audits/${encodeURIComponent(slug)}/confirm`,
        { token: proof },
        { signal: abort.signal, quiet: true },
      )
      .catch(() => undefined);
    return () => abort.abort();
  }, [slug, proof]);

  useEffect(() => {
    const abort = new AbortController();
    // Сводка нужна ради марки, домена и полноты сетки — но отчёт без неё
    // читается, поэтому её отказ не роняет экран.
    const summary = getPublicAudit(slug, abort.signal).catch(() => null);
    Promise.all([
      api
        // `quiet`: путь публичный, и 401 на нём не означает потерю сессии —
        // объявлять её значит выкинуть читателя на форму входа с экрана,
        // который вообще не про вход.
        .get<AuditReport>(`/public/audits/${encodeURIComponent(slug)}/report`, {
          signal: abort.signal,
          quiet: true,
        }),
      summary,
    ])
      .then(([report, audit]) => setLoad({ kind: 'ready', report, summary: audit }))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setLoad({ kind: 'failed', error });
      });
    return () => abort.abort();
  }, [slug]);

  // Отчёт пересылают ссылкой, и вкладка называется именем бренда, а не
  // «Кабинет». Общий `noindex` кабинета для этой страницы снимается: публичный
  // отчёт — витрина продукта, а не личные данные организации.
  const brand = load.kind === 'ready' ? (load.summary?.brand_name ?? '') : '';
  useEffect(() => {
    const previous = document.title;
    document.title = brand
      ? `${brand} в ответах нейросетей — Seeno`
      : 'Отчёт бесплатного аудита — Seeno';
    const robots = document.querySelector('meta[name="robots"]');
    const before = robots?.getAttribute('content') ?? null;
    robots?.setAttribute('content', 'index,follow');
    return () => {
      document.title = previous;
      if (robots && before !== null) robots.setAttribute('content', before);
    };
  }, [brand]);

  if (load.kind === 'loading' || load.kind === 'failed') {
    return (
      <main className="auth">
        <div className="auth-card audit-card">
          <a className="brand" href="https://seeno.ru/">
            <Mark />
            seeno
          </a>
          {load.kind === 'loading' ? (
            <Skeleton rows={4} />
          ) : (
            <Missing error={load.error} />
          )}
        </div>
      </main>
    );
  }

  return <AuditReportBody slug={slug} report={load.report} summary={load.summary} />;
}

/**
 * Отсутствующий и истёкший отчёт — разные разговоры, и оба человеческие.
 *
 * Ключевое: ни в одном из случаев здесь не появляется форма входа. Публичный
 * отчёт не требует сессии, и подсовывать её вместо ответа — это тот самый
 * дефект, ради которого экран и написан.
 */
function Missing({ error }: { error: unknown }) {
  const gone = error instanceof ApiError && error.code === 'gone';
  return (
    <Empty
      eyebrow={gone ? 'Срок истёк' : 'Отчёт не найден'}
      title={gone ? 'Срок этого отчёта вышел' : 'Такого отчёта нет'}
      action={
        <Link className="btn btn-primary" to="/audit">
          Запустить свой аудит
        </Link>
      }
    >
      {gone
        ? `${PUBLIC_REPORT_LIFETIME} Аудит можно запустить заново — это бесплатно.`
        : 'Возможно, ссылка набрана с ошибкой или отчёт был удалён. Свой аудит можно запустить бесплатно и без регистрации.'}
    </Empty>
  );
}

/**
 * Тело отчёта отделено от загрузки и **экспортировано** намеренно: приёмка
 * `tools/check-report-page.mjs` рисует его на готовых данных и проверяет, что
 * полный отчёт даёт все секции, а отчёт прежней сборки — не падает. Через
 * `useEffect` этого не проверить: на сервере эффекты не выполняются.
 */
export function AuditReportBody({
  slug,
  report,
  summary,
}: {
  slug: string;
  report: AuditReport;
  summary: Summary;
}) {
  // Названия движков берутся из ответа, а не из зашитой таблицы: движок —
  // строка справочника, а не перечисление. `GET /engines` здесь недоступен —
  // страница публичная, — поэтому источник один: `by_engine.engine_title`.
  const titles = new Map(
    (report.by_engine ?? []).map((item) => [item.engine_code, item.engine_title]),
  );
  const titleOf = (code: string) => engineName(code, titles.get(code));

  const quotes = report.quotes ?? [];
  const unseen = quotes.filter((quote) => !quote.brand_mentioned).slice(0, QUOTES_UNSEEN);
  const seen = quotes.filter((quote) => quote.brand_mentioned).slice(0, QUOTES_SEEN);

  // Полосы сортируются по значению (data-viz.md §5): график рисуют ради формы
  // рейтинга, а порядок движков в ответе — это порядок справочника, из-за
  // которого «где вас видно» читалось как случайная лесенка. Копия обязательна:
  // `sort` правит массив на месте, а это данные ответа, а не наши.
  //
  // Неизмеренные уходят вниз: у них нет значения, и вклинившись в середину, они
  // рвут ряд, по которому глаз и читает рейтинг.
  const rank = (value: number | null | undefined) =>
    value === null || value === undefined ? Number.NEGATIVE_INFINITY : value;
  const engines = [...(report.by_engine ?? [])].sort(
    (left, right) => rank(right.visibility) - rank(left.visibility),
  );
  const competitors = [...(report.top_competitors ?? [])].sort(
    (left, right) => rank(right.mentions) - rank(left.mentions),
  );
  const checks = report.readiness?.checks ?? [];

  const basis = provenance(report, summary);

  return (
    <main className="report-public">
      <header className="report-nav">
        <div className="wrap">
          <a className="brand" href="https://seeno.ru/">
            <Mark />
            seeno
          </a>
          <ClaimButton slug={slug} small />
        </div>
      </header>

      {/* ── шапка: чей отчёт и какой балл ─────────────────────────────────── */}
      <section className="report-head">
        <div className="wrap">
          <span className="eyebrow">Бесплатный аудит</span>
          <h1 className="display-sm">{summary?.brand_name ?? 'Ваш бренд'}</h1>
          {summary ? (
            <p className="report-domain">
              {summary.website_display || summary.website_domain}
            </p>
          ) : null}

          <figure className="index-fig">
            <div className="audit-score">
              <div className="value tnum">{scoreFmt(report.score)}</div>
              <div className={`audit-status${report.score < 40 ? ' low' : ''}`}>
                {statusLadder(report.score)}
              </div>
            </div>
            {/* Шкала индекса всегда 0–100, поэтому доля равна самому баллу. */}
            <div className="index-track">
              <div
                className="index-fill"
                style={{ '--w': `${report.score}%` } as React.CSSProperties}
              />
            </div>
            <div className="index-ticks" aria-hidden="true">
              {[20, 40, 60, 80].map((tick) => (
                <span key={tick} style={{ '--at': `${tick}%` } as React.CSSProperties}>
                  {tick}
                </span>
              ))}
            </div>
            <figcaption className="stamp">{basis}</figcaption>
          </figure>

          {/* Определение балла было только в кабинете, куда читатель этого
              отчёта ещё не заходил: сюда он попадает по пересланной ссылке и
              видит голое число со словом лестницы. Формулировка та же, что на
              «Обзоре бренда», но без внутренних слов — здесь их не на что
              опереть. */}
          <p className="report-note">
            <b>Что это за балл</b> — шкала 0–100. В него входит, как часто нейросети
            называют бренд в ответах, находят ли вообще, на каком месте называют, во
            скольких нейросетях и ссылаются ли они на сайт.
          </p>

          <div className="report-facts scorecard">
            <div className="metric">
              <strong className="tnum">
                {coverage(report.engines_found, report.engines_total)}
              </strong>
              {/* Подпись и её пояснение называли одну вещь двумя словами:
                  «нейросетей» сверху, «движок» строкой ниже. Это публичная
                  страница отчёта, и читает её человек, который про наш словарь
                  ничего не знает. */}
              <div className="m-label">Нейросетей нашли бренд</div>
              <p className="m-hint">
                Нейросеть засчитана, если назвала бренд хотя бы в одном ответе.
              </p>
            </div>
            <div className="metric">
              <strong className="tnum">
                {report.first_place_share === null ||
                report.first_place_share === undefined
                  ? NOT_MEASURED
                  : percent(report.first_place_share)}
              </strong>
              <div className="m-label">Ответов с первым местом</div>
              <p className="m-hint">
                Доля ответов, где бренд назван раньше всех остальных.
              </p>
            </div>
            <div className="metric">
              <strong>{report.cites_own_domain ? 'Да' : 'Нет'}</strong>
              <div className="m-label">ИИ ссылается на сайт</div>
              <p className="m-hint">
                {report.cites_own_domain
                  ? 'Хотя бы один движок привёл ваш домен источником.'
                  : 'Ни один движок не привёл ваш домен источником ответа.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── цитаты, где бренд не назвали ──────────────────────────────────── */}
      {unseen.length ? (
        <section className="special">
          <div className="wrap">
            {/* Шапка секции обёрнута в `.head` — как у светлого соседа «Ответы с
                вашим брендом». Без обёртки подводке не на чем нести отступ и
                меру: она шла к первой цитате вплотную и растягивалась на все
                1180 px (около 67em при норме 32em для кириллицы). `.head` даёт
                и то и другое — 44em и 40–60 px — механизмом, который система
                уже описала, вместо нового margin. */}
            <div className="head">
              <span className="eyebrow on-dark">Где вас не назвали</span>
              <h2 className="h2">Ответы без вашего бренда</h2>
              <p className="intro">
                Так нейросети отвечают покупателю, который вас ещё не знает. Названы
                другие — значит, выбор произошёл без вас.
              </p>
            </div>
            <div className="quote-list">
              {unseen.map((quote, index) => (
                <QuoteBlock
                  key={`unseen-${index}`}
                  quote={quote}
                  engine={titleOf(quote.engine_code)}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── цитаты, где бренд назвали ─────────────────────────────────────── */}
      {seen.length ? (
        <section className="sec">
          <div className="wrap">
            <div className="head">
              <span className="eyebrow">Где вас назвали</span>
              <h2 className="h2">Ответы с вашим брендом</h2>
              <p>Дословно, как их получил покупатель. Правок мы не вносим.</p>
            </div>
            <div className="quote-list">
              {seen.map((quote, index) => (
                <QuoteBlock
                  key={`seen-${index}`}
                  quote={quote}
                  engine={titleOf(quote.engine_code)}
                />
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {/* ── конкуренты ────────────────────────────────────────────────────── */}
      {competitors.length ? (
        <section className="board">
          <div className="wrap">
            <div className="head">
              <span className="eyebrow on-dark">Кого называют вместо вас</span>
              <h2 className="h2">Эти марки нейросети называют чаще</h2>
              <p>
                Счёт упоминаний в тех же ответах. Полоса — доля от самого частого, число
                рядом абсолютное.
              </p>
            </div>
            <figure>
              <div className="board-rows">
                {withBars(competitors.map((item) => item.mentions)).map((bar, index) => {
                  const item = competitors[index]!;
                  return (
                    <div className="brow" key={item.name}>
                      <div className="brow-name">
                        {item.name}
                        {item.share === null || item.share === undefined ? null : (
                          <span>доля голоса {percent(item.share)}</span>
                        )}
                      </div>
                      <div className="brow-track">
                        <div
                          className={`brow-fill${bar.lead ? ' top' : ''}`}
                          style={{ '--w': bar.width } as React.CSSProperties}
                        />
                      </div>
                      <div className="brow-val tnum">{item.mentions}</div>
                    </div>
                  );
                })}
              </div>
              <figcaption className="board-cap">{basis}</figcaption>
            </figure>
          </div>
        </section>
      ) : null}

      {/* ── движки ────────────────────────────────────────────────────────── */}
      {engines.length ? (
        <section className="sec">
          <div className="wrap">
            <div className="head">
              <span className="eyebrow">По движкам</span>
              <h2 className="h2">Где именно вас видно</h2>
              <p>
                Доля ответов движка, в которых бренд назван. Движки не различаются цветом:
                одна шкала, одна заливка, сортировка по значению.
              </p>
            </div>
            <figure>
              <div className="bars">
                {withBars(engines.map((item) => item.visibility ?? null)).map(
                  (bar, index) => {
                    const item = engines[index]!;
                    return (
                      <div
                        className={`brow${bar.measured ? '' : ' na'}`}
                        key={item.engine_code}
                      >
                        <div className="brow-name">
                          {engineName(item.engine_code, item.engine_title)}
                          <span>
                            {item.mentioned
                              ? item.best_position === null ||
                                item.best_position === undefined
                                ? 'назвали'
                                : `лучшее место — ${item.best_position}`
                              : 'не назвали ни разу'}
                          </span>
                        </div>
                        <div className="brow-track">
                          <div
                            className={`brow-fill${bar.lead ? ' top' : ''}`}
                            style={{ '--w': bar.width } as React.CSSProperties}
                          />
                        </div>
                        <div className="brow-val tnum">
                          {bar.measured ? percent(item.visibility ?? 0) : NOT_MEASURED}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
              <figcaption className="stamp">{basis}</figcaption>
            </figure>
          </div>
        </section>
      ) : null}

      {/* ── готовность сайта ──────────────────────────────────────────────── */}
      {checks.length ? (
        <section className="sec">
          <div className="wrap">
            <div className="head">
              <span className="eyebrow">Готовность сайта</span>
              <h2 className="h2">Что нейросети находят на сайте</h2>
              <p>
                Проверки, которые продукт выполняет машинно. Каждая — то, чем ответ
                нейросети можно подкрепить ссылкой на вас.
              </p>
            </div>
            <div className="index-fig">
              <div className="audit-score">
                <div className="value tnum">
                  {typeof report.readiness?.score === 'number'
                    ? scoreFmt(report.readiness.score)
                    : '—'}
                </div>
                {/* «5 из 9 пройдено» не называло, чего именно пять: балл рядом
                    считается по проверкам, и знаменатель обязан быть подписан
                    так же, как везде в продукте. */}
                <div className="audit-status">
                  {coverageOf(
                    report.readiness?.checks_passed ?? 0,
                    report.readiness?.checks_total ?? checks.length,
                    'проверка',
                    'проверки',
                    'проверок',
                  )}{' '}
                  пройдено
                </div>
              </div>
            </div>
            <ul className="checks">
              {checks.map((check) => (
                <li key={check.code}>
                  <span className="check-title">{check.title}</span>
                  <span className={`check-verdict ${verdictClass(check.result)}`}>
                    {verdictWord(check.result)}
                  </span>
                  {check.evidence ? (
                    <p className="check-note stamp tech-string">{check.evidence}</p>
                  ) : check.description ? (
                    <p className="check-note stamp">{check.description}</p>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}

      {/* ── призыв ────────────────────────────────────────────────────────── */}
      <section className="final report-final">
        <div className="wrap">
          <span className="eyebrow on-dark center">Это один замер</span>
          <h2 className="h2">Дальше — каждую неделю</h2>
          <p>
            Продукт меряет то же самое по расписанию, показывает, кого называют вместо
            вас, и что с этим делать. Отчёт можно забрать в кабинет — он станет первой
            точкой ряда.
          </p>
          <ClaimButton slug={slug} />
        </div>
      </section>
    </main>
  );
}

/**
 * Кнопка «забрать» ведёт на присвоение, а не на регистрацию.
 *
 * До этой правки она вела на `/auth/signup` без слага: вошедшего маршрут
 * `/auth/*` тут же уводил на обзор, гость после регистрации попадал на пустой
 * кабинет — и в обоих случаях отчёт оставался ничьим. Теперь адрес один и тот
 * же для всех: гостя гостевая ветка маршрутов сама отправит на вход, запомнив
 * путь, и вернёт сюда же после него.
 *
 * Слаг дополнительно кладётся в хранилище: состояние навигации не переживает
 * ухода на Яндекс ID и возврата, а подтверждение почты вообще открывают из
 * письма — часто в другой вкладке.
 */
function ClaimButton({ slug, small }: { slug: string; small?: boolean }) {
  return (
    <Link
      className={`btn btn-primary${small ? ' btn-sm' : ''}`}
      to={`/audits/claim?slug=${encodeURIComponent(slug)}`}
      onClick={() => {
        rememberClaim(slug);
        try {
          sessionStorage.setItem(AUDIT_SLUG_KEY, slug);
        } catch {
          // Приватный режим запрещает запись — присвоение переживёт: слаг
          // едет в адресе, а хранилище здесь только страховка.
        }
      }}
    >
      Забрать результат в кабинет
    </Link>
  );
}

function QuoteBlock({ quote, engine }: { quote: AuditQuote; engine: string }) {
  return (
    <div>
      <blockquote className="quote">
        {quote.excerpt}
        <footer className="micro">
          {engine} · {quote.question}
        </footer>
      </blockquote>
      {/* Имена конкурентов вынесены в отдельный элемент, а не слиты в строку:
          это главное, ради чего человек читает эту секцию, и приглушённым
          серым оно терялось рядом с самой цитатой. Цвет и насыщенность даёт
          `.quote-named`. */}
      <p className="stamp quote-said">
        {quote.brand_mentioned ? (
          'Ваш бренд назван'
        ) : quote.competitors_named?.length ? (
          <>
            Вас не назвали. Названы:{' '}
            <b className="quote-named">{quote.competitors_named.join(', ')}</b>
          </>
        ) : (
          'Вас не назвали'
        )}
      </p>
    </div>
  );
}

/** Геометрия полос выборки: знаменатель один на блок (data-viz.md §3). */
function withBars(values: readonly (number | null)[]) {
  return barGeometries(values.map((value) => ({ value })));
}

/**
 * Основание под числами: что мерили, сколько движков, насколько полной вышла
 * сетка и когда собран отчёт (закон 7 дизайн-системы).
 *
 * Строка одна на страницу и повторяется под каждым графиком, потому что отчёт
 * печатают: на бумаге подпись под графиком — единственное место, где читатель
 * ещё может узнать основание.
 */
function provenance(report: AuditReport, summary: Summary): string {
  const parts = ['Индекс ИИ-видимости'];
  const engines = report.methodology?.engines ?? report.engines_total;
  if (engines) parts.push(countOf(engines, 'движок', 'движка', 'движков'));
  const questions = report.methodology?.questions ?? summary?.questions_planned;
  if (questions) parts.push(countOf(questions, 'вопрос', 'вопроса', 'вопросов'));
  // Полнота сетки: сколько клеток «вопрос × движок» реально ответили. Оба
  // счётчика необязательны в контракте, поэтому строка появляется только когда
  // есть чем её наполнить — выдуманная полнота хуже её отсутствия.
  if (
    typeof summary?.tasks_total === 'number' &&
    typeof summary.tasks_done === 'number'
  ) {
    if (summary.tasks_total > 0) {
      parts.push(
        `${coverageOf(summary.tasks_done, summary.tasks_total, 'ответ', 'ответа', 'ответов')}`,
      );
    }
  }
  parts.push('1 прогон');
  const when = summary?.ready_at ?? summary?.created_at;
  if (when) parts.push(`собран ${dateProse(when)}`);
  return parts.join(' · ');
}

function verdictWord(result: 'passed' | 'failed' | 'unknown'): string {
  if (result === 'passed') return 'Есть';
  if (result === 'failed') return 'Нет';
  return NOT_MEASURED;
}

function verdictClass(result: 'passed' | 'failed' | 'unknown'): string {
  if (result === 'passed') return 'yes';
  if (result === 'failed') return 'no';
  return 'unknown';
}
